import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import {
  getKalkulatorProvinsi,
  getKalkulatorKabupaten,
  getKalkulatorJenisLayanan,
  getKalkulatorJenisProduk,
  hitungBiayaSH,
  getConfig,
} from "../services/api";

const formatIDR = (amount) => {
  if (amount === null || amount === undefined) return "Rp 0";
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(amount);
};

const KalkulatorBiaya = () => {
  const [provinsiList, setProvinsiList] = useState([]);
  const [kabupatenList, setKabupatenList] = useState([]);
  const [jenisLayananList, setJenisLayananList] = useState([]);
  const [jenisProdukList, setJenisProdukList] = useState([]);

  const [form, setForm] = useState({
    provinsi_id: "",
    kabupaten_id: "",
    jenis_layanan: "",
    jenis_produk: "",
    skala_usaha: "",
    jumlah_produk: "",
    jumlah_pabrik: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [hasil, setHasil] = useState(null);
  const [tiketPesawat, setTiketPesawat] = useState(0);
  const [lphName, setLphName] = useState("");

  useEffect(() => {
    getConfig()
      .then((res) => setLphName(res.data?.LPH_NAME || ""))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const [provRes, layananRes] = await Promise.all([
          getKalkulatorProvinsi(),
          getKalkulatorJenisLayanan(),
        ]);
        setProvinsiList(provRes.data?.data || []);
        setJenisLayananList(
          layananRes.data?.data?.data || layananRes.data?.data || [],
        );
      } catch {
        setAlert({
          type: "danger",
          message: "Gagal memuat data awal. Silakan refresh halaman.",
        });
      } finally {
        setInitLoading(false);
      }
    };
    init();
  }, []);

  const handleProvinsiChange = async (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, provinsi_id: value, kabupaten_id: "" }));
    setKabupatenList([]);
    if (value) {
      try {
        const res = await getKalkulatorKabupaten(value);
        setKabupatenList(res.data?.data || []);
      } catch {
        setAlert({ type: "danger", message: "Gagal memuat data kabupaten." });
      }
    }
  };

  const handleJenisLayananChange = async (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, jenis_layanan: value, jenis_produk: "" }));
    setJenisProdukList([]);
    if (value) {
      try {
        const res = await getKalkulatorJenisProduk(value);
        setJenisProdukList(res.data?.data?.data || res.data?.data || []);
      } catch {
        setAlert({
          type: "danger",
          message: "Gagal memuat data jenis produk.",
        });
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((err) => ({ ...err, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.provinsi_id) newErrors.provinsi_id = "Provinsi wajib dipilih";
    if (!form.kabupaten_id)
      newErrors.kabupaten_id = "Kabupaten / Kota wajib dipilih";
    if (!form.jenis_layanan)
      newErrors.jenis_layanan = "Jenis Layanan wajib dipilih";
    if (!form.jenis_produk)
      newErrors.jenis_produk = "Jenis Produk wajib dipilih";
    if (!form.skala_usaha) newErrors.skala_usaha = "Skala Usaha wajib dipilih";
    if (!form.jumlah_produk || parseInt(form.jumlah_produk) <= 0)
      newErrors.jumlah_produk = "Jumlah Produk wajib diisi";
    if (!form.jumlah_pabrik || parseInt(form.jumlah_pabrik) <= 0)
      newErrors.jumlah_pabrik = "Jumlah Pabrik / Cabang wajib diisi";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setLoading(true);
    setAlert(null);
    setHasil(null);
    try {
      const res = await hitungBiayaSH({
        ...form,
        jumlah_produk: parseInt(form.jumlah_produk),
        jumlah_pabrik: parseInt(form.jumlah_pabrik),
      });
      const payload = res.data?.data;
      const data =
        payload?.data ||
        (payload && !payload.code ? payload : null) ||
        res.data?.data?.data;
      if (data) {
        setHasil(data);
        setTiketPesawat(data.tiket_pesawat || 0);
      } else {
        setAlert({
          type: "danger",
          message: "Gagal mendapatkan hasil perhitungan.",
        });
      }
    } catch (err) {
      const responseData = err.response?.data;
      const detailMessage =
        responseData?.detail?.detail?.message ||
        responseData?.detail?.message ||
        (typeof responseData?.detail === "string"
          ? responseData.detail
          : null);
      const message =
        [responseData?.message, detailMessage].filter(Boolean).join(" ") ||
        "Terjadi kesalahan saat menghitung.";
      setAlert({
        type: "danger",
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  const isKecilMikro =
    hasil?.skala_usaha === "JU.3" || hasil?.skala_usaha === "JU.4";

  const grandTotal = hasil
    ? (hasil.total_cabang_pertama || 0) -
      (hasil.tiket_pesawat || 0) +
      tiketPesawat
    : 0;

  return (
    <div
      style={{
        paddingTop: 180,
        minHeight: "100vh",
        paddingBottom: 50,
        backgroundColor: "#f9fafb",
      }}
    >
      <Container>
        {alert && (
          <Alert
            variant={alert.type}
            onClose={() => setAlert(null)}
            dismissible
          >
            {alert.message}
          </Alert>
        )}

        {initLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Memuat data...</p>
          </div>
        ) : (
          <Row>
            {/* Form Kiri */}
            <Col md={6}>
              <div style={styles.card}>
                <h2 style={styles.cardTitle}>Form Perhitungan Biaya</h2>
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label style={styles.label}>
                      Provinsi Fasilitas Produksi
                    </Form.Label>
                    <Form.Select
                      name="provinsi_id"
                      value={form.provinsi_id}
                      onChange={handleProvinsiChange}
                      isInvalid={!!errors.provinsi_id}
                    >
                      <option value="">Pilih Provinsi</option>
                      {provinsiList.map((p) => (
                        <option key={p.kodeprovinsi} value={p.kodeprovinsi}>
                          {p.namaprovinsi}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.provinsi_id}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={styles.label}>
                      Kabupaten / Kota Fasilitas Produksi
                    </Form.Label>
                    <Form.Select
                      name="kabupaten_id"
                      value={form.kabupaten_id}
                      onChange={handleChange}
                      isInvalid={!!errors.kabupaten_id}
                      disabled={!form.provinsi_id || kabupatenList.length === 0}
                    >
                      <option value="">Pilih Kabupaten / Kota</option>
                      {kabupatenList.map((k) => (
                        <option key={k.kodekabupaten} value={k.kodekabupaten}>
                          {k.namakabupaten}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.kabupaten_id}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={styles.label}>Jenis Layanan</Form.Label>
                    <Form.Select
                      name="jenis_layanan"
                      value={form.jenis_layanan}
                      onChange={handleJenisLayananChange}
                      isInvalid={!!errors.jenis_layanan}
                    >
                      <option value="">Pilih Jenis Layanan</option>
                      {jenisLayananList.map((j) => (
                        <option key={j.code} value={j.code}>
                          {j.name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.jenis_layanan}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={styles.label}>Jenis Produk</Form.Label>
                    <Form.Select
                      name="jenis_produk"
                      value={form.jenis_produk}
                      onChange={handleChange}
                      isInvalid={!!errors.jenis_produk}
                      disabled={
                        !form.jenis_layanan || jenisProdukList.length === 0
                      }
                    >
                      <option value="">Pilih Jenis Produk</option>
                      {jenisProdukList.map((j) => (
                        <option key={j.code} value={j.code}>
                          {j.name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.jenis_produk}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={styles.label}>Skala Usaha</Form.Label>
                    <Form.Select
                      name="skala_usaha"
                      value={form.skala_usaha}
                      onChange={handleChange}
                      isInvalid={!!errors.skala_usaha}
                    >
                      <option value="">Pilih...</option>
                      <option value="JU.1">Besar</option>
                      <option value="JU.2">Menengah</option>
                      <option value="JU.3">Kecil</option>
                      <option value="JU.4">Mikro</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.skala_usaha}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={styles.label}>Jumlah Produk</Form.Label>
                    <Form.Control
                      type="number"
                      name="jumlah_produk"
                      value={form.jumlah_produk}
                      onChange={handleChange}
                      placeholder="Isi jumlah produk"
                      min="1"
                      isInvalid={!!errors.jumlah_produk}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.jumlah_produk}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={styles.label}>
                      Jumlah Pabrik / Cabang
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="jumlah_pabrik"
                      value={form.jumlah_pabrik}
                      onChange={handleChange}
                      placeholder="Isi jumlah pabrik / cabang"
                      min="1"
                      isInvalid={!!errors.jumlah_pabrik}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.jumlah_pabrik}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={styles.label}>
                      Lembaga Pemeriksa Halal (LPH)
                    </Form.Label>
                    <div style={styles.lphDisplay}>{lphName}</div>
                  </Form.Group>

                  <Button
                    type="submit"
                    disabled={loading}
                    style={styles.btnPrimary}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Menghitung...
                      </>
                    ) : (
                      "Hitung"
                    )}
                  </Button>
                </Form>
              </div>
            </Col>

            {/* Hasil Kanan */}
            <Col md={6}>
              <div style={styles.card}>
                <h2 style={styles.cardTitle}>Detail Hasil Perhitungan</h2>

                {!hasil ? (
                  <p className="text-muted text-center py-4">
                    Isi form di sebelah kiri dan klik <strong>Hitung</strong>{" "}
                    untuk melihat hasil.
                  </p>
                ) : (
                  <>
                    {/* Mandays x Unit Cost */}
                    <CalcRow
                      label1="Mandays"
                      val1={String(hasil.mandays || 0)}
                      label2="Unit Cost"
                      val2={formatIDR(
                        hasil.mandays > 0 ? hasil.unit_cost / hasil.mandays : 0,
                      )}
                      result={formatIDR(hasil.unit_cost)}
                    />

                    {/* Operasional */}
                    <CalcRowSingle
                      label="Operasional"
                      val={formatIDR(hasil.operasional)}
                      result={formatIDR(hasil.operasional)}
                    />

                    {/* UHPD */}
                    <CalcRow
                      label1={null}
                      val1={String(isKecilMikro ? 1 : hasil.mandays)}
                      label2="UHPD"
                      val2={formatIDR(
                        isKecilMikro ? hasil.uhpd : hasil.uhpd_mb,
                      )}
                      result={formatIDR(hasil.uhpd)}
                    />

                    {/* Transportasi */}
                    <CalcRow
                      label1={null}
                      val1={String(isKecilMikro ? 1 : hasil.mandays)}
                      label2="Transportasi"
                      val2={formatIDR(
                        isKecilMikro ? hasil.transport : hasil.transport_mb,
                      )}
                      result={formatIDR(hasil.transport)}
                    />

                    {/* Akomodasi */}
                    <CalcRow
                      label1={null}
                      val1={String(isKecilMikro ? 1 : hasil.mandays)}
                      label2="Akomodasi"
                      val2={formatIDR(
                        isKecilMikro ? hasil.akomodasi : hasil.akomodasi_mb,
                      )}
                      result={formatIDR(hasil.akomodasi)}
                    />

                    {/* Tiket Pesawat */}
                    <div style={styles.calcRow}>
                      <div></div>
                      <div></div>
                      <div>
                        <div style={styles.calcLabel}>Tiket Pesawat</div>
                        <Form.Select
                          value={tiketPesawat}
                          onChange={(e) =>
                            setTiketPesawat(Number(e.target.value))
                          }
                          style={{ borderRadius: "0.5rem", fontSize: "0.9rem" }}
                        >
                          <option value={0}>Tidak memerlukan tiket</option>
                          {hasil.tiket_pesawat > 0 && (
                            <option value={hasil.tiket_pesawat}>
                              {formatIDR(hasil.tiket_pesawat)}
                            </option>
                          )}
                        </Form.Select>
                      </div>
                      <div style={styles.calcOperator}>=</div>
                      <div>
                        <div style={styles.calcBox}>
                          {formatIDR(tiketPesawat)}
                        </div>
                      </div>
                    </div>

                    <hr
                      style={{
                        borderTop: "2px solid #e2e8f0",
                        margin: "1.5rem 0",
                      }}
                    />

                    {/* BPJPH */}
                    <CalcRowSingle
                      label="Pendaftaran, Verifikasi, Penerbitan SH"
                      val={formatIDR(hasil.biaya_bpjph)}
                      result={formatIDR(hasil.biaya_bpjph)}
                    />

                    {/* MUI */}
                    <CalcRowSingle
                      label="Penetapan KH"
                      val={formatIDR(hasil.biaya_mui)}
                      result={formatIDR(hasil.biaya_mui)}
                    />

                    <hr
                      style={{
                        borderTop: "2px solid #e2e8f0",
                        margin: "1.5rem 0",
                      }}
                    />

                    {/* Grand Total */}
                    <div style={styles.grandTotalRow}>
                      <div style={styles.grandTotalLabel}>Grand Total</div>
                      <div style={{ ...styles.calcOperator, color: "white" }}>
                        =
                      </div>
                      <div style={styles.grandTotalBox}>
                        {formatIDR(grandTotal)}
                      </div>
                    </div>

                    <div style={styles.disclaimer}>
                      <strong>Catatan:</strong> Perhitungan biaya mengacu pada
                      Kep Kaban No. 22 Tahun 2024 sebagai tarif batas atas. LPH
                      dapat menyesuaikan biaya sesuai kebijakan masing-masing.
                    </div>
                  </>
                )}
              </div>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

// Sub-komponen baris kalkulasi: [val1] x [label2 / val2] = [result]
const CalcRow = ({ label1, val1, label2, val2, result }) => (
  <div style={styles.calcRow}>
    <div>
      {label1 && <div style={styles.calcLabel}>{label1}</div>}
      <div style={styles.calcBox}>{val1}</div>
    </div>
    <div style={styles.calcOperator}>&times;</div>
    <div>
      {label2 && <div style={styles.calcLabel}>{label2}</div>}
      <div style={styles.calcBox}>{val2}</div>
    </div>
    <div style={styles.calcOperator}>=</div>
    <div>
      <div style={styles.calcBox}>{result}</div>
    </div>
  </div>
);

// Sub-komponen baris kalkulasi tanpa multiplier: [label / val] = [result]
const CalcRowSingle = ({ label, val, result }) => (
  <div style={styles.calcRow}>
    <div></div>
    <div></div>
    <div>
      {label && <div style={styles.calcLabel}>{label}</div>}
      <div style={styles.calcBox}>{val}</div>
    </div>
    <div style={styles.calcOperator}>=</div>
    <div>
      <div style={styles.calcBox}>{result}</div>
    </div>
  </div>
);

const styles = {
  card: {
    border: "1px solid #e5e7eb",
    borderRadius: "0.75rem",
    backgroundColor: "white",
    padding: "2rem",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    marginBottom: "1.5rem",
  },
  cardTitle: {
    color: "#7c3aed",
    fontSize: "1.5rem",
    fontWeight: 700,
    marginBottom: "1.5rem",
    paddingBottom: "0.75rem",
    borderBottom: "2px solid #7c3aed",
  },
  label: {
    display: "block",
    fontWeight: 500,
    marginBottom: "0.5rem",
    fontSize: "0.95rem",
    color: "#4a5568",
  },
  lphDisplay: {
    width: "100%",
    borderRadius: "0.5rem",
    border: "1px solid #cbd5e0",
    padding: "0.65rem 0.875rem",
    backgroundColor: "#f3f4f6",
    fontSize: "0.95rem",
    color: "#4a5568",
    fontWeight: 500,
  },
  btnPrimary: {
    width: "100%",
    backgroundColor: "#7c3aed",
    color: "white",
    padding: "0.75rem",
    borderRadius: "0.5rem",
    fontWeight: 600,
    border: "none",
    cursor: "pointer",
  },
  calcRow: {
    display: "grid",
    gridTemplateColumns: "90px 30px 1fr 30px 1fr",
    alignItems: "center",
    gap: "0.75rem",
    marginBottom: "1rem",
    padding: "0.75rem",
    backgroundColor: "#f8fafc",
    borderRadius: "0.5rem",
  },
  calcLabel: {
    color: "#64748b",
    fontSize: "0.8rem",
    fontWeight: 500,
    textAlign: "center",
    marginBottom: "0.25rem",
  },
  calcBox: {
    backgroundColor: "white",
    border: "1.5px solid #cbd5e0",
    borderRadius: "0.5rem",
    padding: "0.65rem",
    textAlign: "center",
    fontSize: "0.9rem",
    color: "#2d3748",
    fontWeight: 500,
    minHeight: 42,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  calcOperator: {
    textAlign: "center",
    fontWeight: 600,
    fontSize: "1.1rem",
    color: "#4a5568",
  },
  grandTotalRow: {
    display: "grid",
    gridTemplateColumns: "1fr 30px 1fr",
    gap: "1rem",
    padding: "1.25rem",
    background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
    borderRadius: "0.5rem",
    marginTop: "1.5rem",
  },
  grandTotalLabel: {
    fontWeight: 700,
    color: "white",
    fontSize: "1.1rem",
    display: "flex",
    alignItems: "center",
  },
  grandTotalBox: {
    backgroundColor: "white",
    borderRadius: "0.5rem",
    padding: "0.75rem",
    textAlign: "center",
    fontSize: "1.1rem",
    color: "#7c3aed",
    fontWeight: 700,
  },
  disclaimer: {
    marginTop: "1.5rem",
    padding: "1rem",
    backgroundColor: "#fef3c7",
    borderLeft: "4px solid #f59e0b",
    borderRadius: "0.5rem",
    fontSize: "0.875rem",
    color: "#78350f",
  },
};

export default KalkulatorBiaya;
